import { useState, useEffect } from "react";
import { FastAverageColor } from "fast-average-color";

interface GradientResult {
  gradient: string;
  isDark: boolean;
  loaded: boolean;
}

const BRAND_GRADIENT = "linear-gradient(to bottom, hsl(222, 47%, 25%) 0%, hsl(222, 47%, 45%) 40%, transparent 100%)";
const cache = new Map<string, { gradient: string; isDark: boolean }>();

/**
 * Extract dominant color from an avatar image URL and return a vertical gradient.
 * Falls back to brand blue gradient if extraction fails or no URL provided.
 */
export function useAvatarGradient(avatarUrl?: string | null): GradientResult {
  const [result, setResult] = useState<GradientResult>({
    gradient: BRAND_GRADIENT,
    isDark: true,
    loaded: !avatarUrl,
  });

  useEffect(() => {
    if (!avatarUrl) {
      setResult({ gradient: BRAND_GRADIENT, isDark: true, loaded: true });
      return;
    }

    // Check cache
    const cached = cache.get(avatarUrl);
    if (cached) {
      setResult({ ...cached, loaded: true });
      return;
    }

    let cancelled = false;
    const fac = new FastAverageColor();

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = avatarUrl;

    img.onload = () => {
      if (cancelled) return;
      try {
        const color = fac.getColor(img);
        const [r, g, b] = color.value;
        const isDark = color.isDark;

        // Build gradient: dominant color (solid) → dominant color (transparent) → fully transparent
        const gradient = `linear-gradient(to bottom, rgba(${r},${g},${b},0.85) 0%, rgba(${r},${g},${b},0.4) 50%, transparent 100%)`;

        const entry = { gradient, isDark };
        cache.set(avatarUrl, entry);
        setResult({ ...entry, loaded: true });
      } catch {
        setResult({ gradient: BRAND_GRADIENT, isDark: true, loaded: true });
      }
      fac.destroy();
    };

    img.onerror = () => {
      if (!cancelled) setResult({ gradient: BRAND_GRADIENT, isDark: true, loaded: true });
      fac.destroy();
    };

    return () => { cancelled = true; };
  }, [avatarUrl]);

  return result;
}
